import { delay as promiseDelay } from '@/utils/debugUtils'

export interface UseAsyncStateReturnBase<Data, Params extends unknown[]> {
  state: Data
  isReady: boolean
  isLoading: boolean
  error: unknown
  execute: (delay?: number, ...args: Params) => Promise<Data>
}

export type UseAsyncStateReturn<Data, Params extends unknown[]> =
  UseAsyncStateReturnBase<Data, Params>

export interface UseAsyncStateOptions<Shallow extends boolean, D = unknown> {
  /**
   * Delay for executing the promise. In milliseconds.
   *
   * @default 0
   */
  delay?: number

  /**
   * Execute the promise right after the function is invoked.
   * Will apply the delay if any.
   *
   * When set to false, you will need to execute it manually.
   *
   * @default true
   */
  immediate?: boolean

  /**
   * Callback when error is caught.
   */
  onError?: (e: unknown) => void

  /**
   * Callback when success is caught.
   * @param {D} data
   */
  onSuccess?: (data: D) => void

  /**
   * Sets the state to initialState before executing the promise.
   *
   * This can be useful when calling the execute function more than once (for
   * example, to refresh data). When set to false, the current state remains
   * unchanged until the promise resolves.
   *
   * @default true
   */
  resetOnExecute?: boolean

  /**
   * Use shallowRef.
   *
   * @default true
   */
  shallow?: Shallow
  /**
   *
   * An error is thrown when executing the execute function
   *
   * @default false
   */
  throwError?: boolean
}

/**
 * Reactive async state. Will not block your setup function and will trigger changes once
 * the promise is ready.
 *
 * @param promise         The promise / async function to be resolved
 * @param initialState    The initial state, used until the first evaluation finishes
 * @param options
 */
export function useAsyncState<Data, Params extends unknown[] = [], Shallow extends boolean = true>(
  promise: Promise<Data> | ((...args: Params) => Promise<Data>),
  initialState: Data,
  options?: UseAsyncStateOptions<Shallow, Data>,
): UseAsyncStateReturn<Data, Params> {
  const validOptions = options || {}
  const {
    immediate = true,
    delay = 0,
    onError = () => {},
    onSuccess = () => {},
    resetOnExecute = true,
    throwError,
  } = validOptions

  const stateReturn = {
    state: initialState,
    isReady: false,
    isLoading: false,
    error: null as unknown | null,
    execute,
  }

  async function execute(delay = 0, ...args: Params) {
    if (resetOnExecute) {
      stateReturn.state = initialState
      stateReturn.error = null
      stateReturn.isReady = false
      stateReturn.isLoading = true
    }

    if (delay > 0) {
      await promiseDelay(delay)
    }

    const resolvedPromise = typeof promise === 'function'
      ? promise(...args)
      : promise

    try {
      const data = await resolvedPromise
      stateReturn.state = data
      stateReturn.isReady = true
      onSuccess(data)
    } catch (e) {
      stateReturn.error = e
      onError(e)
      if (throwError) {
        throw e
      }
    } finally {
      stateReturn.isLoading = false
    }

    return stateReturn.state
  }

  const emptyArgs = [] as unknown as Params
  if (immediate) execute(delay, ...emptyArgs)

  const shell: UseAsyncStateReturnBase<Data, Params> = stateReturn

  return shell
}
